import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Card, Tag, Space, Collapse, Spin } from 'antd';
import { CodeOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import { useCategoryList } from '../../../hooks/categorySetup';
import { useProductList } from '../../../hooks/productSetup';
import { getConfigOptionListByCategory } from '../../../api/configOption';
import { generateJsonFlow, IJsonFlow } from '../../../utils/jsonFlowGenerator';
import { IConfigOption } from '../../../types/configOption';

const { Panel } = Collapse;

interface JsonFlowViewerProps {
  open: boolean;
  onClose: () => void;
}

export const JsonFlowViewer: React.FC<JsonFlowViewerProps> = ({ open, onClose }) => {
  const [viewMode, setViewMode] = useState<'visual' | 'json'>('visual');
  const [allOptions, setAllOptions] = useState<IConfigOption[]>([]);
  const [loading, setLoading] = useState(false);

  const { data: categoriesData } = useCategoryList();
  const categories = categoriesData?.value || [];

  const { data: productsData } = useProductList({
    queryParams: { current: 1, pageSize: 100 }
  });
  const products = productsData?.value || [];

  // Fetch all options when modal opens
  useEffect(() => {
    if (open && categories.length > 0) {
      const fetchAllOptions = async () => {
        setLoading(true);
        try {
          const optionsPromises = categories.map((category) =>
            getConfigOptionListByCategory(category.dogma_categoryid)
          );
          const optionsArrays = await Promise.all(optionsPromises);
          const flatOptions = optionsArrays.flat();
          setAllOptions(flatOptions);
        } catch (error) {
          toast.error('Failed to fetch options');
        } finally {
          setLoading(false);
        }
      };
      fetchAllOptions();
    }
  }, [open, categories]);

  const jsonFlow: IJsonFlow = useMemo(() => {
    return generateJsonFlow(categories, allOptions, products);
  }, [categories, allOptions, products]);
  const handleCopyJson = () => {
    const jsonString = JSON.stringify(jsonFlow, null, 2);
    navigator.clipboard.writeText(jsonString);
    toast.success('JSON copied to clipboard');
  };

  const handleDownloadJson = () => {
    const jsonString = JSON.stringify(jsonFlow, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `configuration-flow-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('JSON file downloaded');
  };

  return (
    <Modal
      title={
        <Space>
          <CodeOutlined />
          <span>Configuration Flow Visualization</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div className="mb-4">
        <Space>
          <Button
            type={viewMode === 'visual' ? 'primary' : 'default'}
            size="small"
            onClick={() => setViewMode('visual')}
          >
            Visual View
          </Button>
          <Button
            type={viewMode === 'json' ? 'primary' : 'default'}
            size="small"
            onClick={() => setViewMode('json')}
          >
            JSON View
          </Button>
          <Button size="small" icon={<CopyOutlined />} onClick={handleCopyJson}>
            Copy JSON
          </Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={handleDownloadJson}>
            Download JSON
          </Button>
        </Space>
      </div>

      <div className="mb-3">
        <Space>
          <Tag color="blue">Product: {jsonFlow.productLine}</Tag>
          <Tag color="green">Questions: {jsonFlow.metadata.totalQuestions}</Tag>
          <Tag color="purple">Categories: {jsonFlow.metadata.totalCategories}</Tag>
          <Tag color="orange">Generated: {new Date(jsonFlow.metadata.generatedAt).toLocaleString()}</Tag>
        </Space>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" tip="Loading configuration..." />
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {viewMode === 'visual' ? (
            <Collapse accordion>
              {jsonFlow.questionFlow.map((question) => (
                <Panel
                  key={question.questionId}
                  header={
                    <Space>
                      <Tag color="purple">Step {question.step}</Tag>
                      <Tag color="geekblue">{question.questionId}</Tag>
                      <span className="font-medium">{question.question}</span>
                      <Tag>{question.questionType}</Tag>
                      <Tag color="cyan">{question.options.length} options</Tag>
                    </Space>
                  }
                >
                  <div className="mb-3 space-y-2">
                    <div>
                      <strong>Category:</strong> <Tag color="blue">{question.categoryName}</Tag>
                    </div>
                    <div>
                      <strong>Option Type:</strong> <Tag color="green">{question.optionTypeName}</Tag>
                    </div>
                    <div>
                      <strong>Response Path:</strong>{' '}
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {question.responsePath}
                      </code>
                    </div>
                    <div>
                      <strong>Required:</strong>{' '}
                      <Tag color={question.required ? 'red' : 'default'}>
                        {question.required ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                    {question.displayCondition && (
                      <div>
                        <strong>Question Display Condition:</strong>{' '}
                        <Tag color="orange">Enabled</Tag>
                        {question.displayCondition.validForProducts && question.displayCondition.validForProducts.length > 0 && (
                          <div className="text-xs mt-1">
                            <strong>Valid for:</strong> {question.displayCondition.validForProducts.length} product(s)
                          </div>
                        )}
                        {question.displayCondition.dependsOn && question.displayCondition.dependsOn.length > 0 && (
                          <div className="text-xs mt-1">
                            <strong>Depends on:</strong> {question.displayCondition.dependsOn.length} condition(s)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-3">
                    <strong className="block mb-2">Options:</strong>
                    {question.options.map((option) => (
                      <Card
                        key={option.optionId}
                        size="small"
                        className="mb-2"
                        title={
                          <Space>
                            <Tag color="green">{option.optionId}</Tag>
                            <span>{option.optionText}</span>
                          </Space>
                        }
                      >
                        <div className="space-y-1 text-sm">
                          {option.description && (
                            <div>
                              <strong>Description:</strong> {option.description}
                            </div>
                          )}
                          {(option.priceModifier || option.basePrice !== undefined) && (
                            <div>
                              <strong>{option.basePrice !== undefined ? 'Base Price' : 'Price Modifier'}:</strong>{' '}
                              <span
                                className={`font-semibold ${
                                  (option.priceModifier || 0) > 0
                                    ? 'text-red-500'
                                    : (option.priceModifier || 0) < 0
                                    ? 'text-green-600'
                                    : 'text-gray-500'
                                }`}
                              >
                                {option.priceModifier && option.priceModifier > 0 ? '+' : ''}£
                                {(option.priceModifier || option.basePrice || 0).toFixed(2)}
                              </span>{' '}
                              <Tag>{option.priceType}</Tag>
                            </div>
                          )}
                          <div>
                            <strong>Response Value:</strong>{' '}
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {option.responseValue}
                            </code>
                          </div>
                          <div>
                            <strong>Input Type:</strong> <Tag>{option.inputType}</Tag>
                          </div>
                          {option.displayCondition && (
                            <div>
                              <strong>Has Display Conditions:</strong>{' '}
                              <Tag color="orange">
                                Yes
                              </Tag>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </Panel>
              ))}
            </Collapse>
          ) : (
            <pre
              className="bg-gray-100 p-4 rounded text-xs overflow-auto"
              style={{ maxHeight: '600px' }}
            >
              {JSON.stringify(jsonFlow, null, 2)}
            </pre>
          )}
        </div>
      )}
    </Modal>
  );
};
